/**
 * Profile Validation Composable
 *
 * Checks if a user has completed required profile fields (height, weight, DOB)
 * for features that need these values (e.g., nutrition planning)
 */

import { computed, ref } from "vue";
import store from "@/store";

export function useProfileValidation() {
  const isChecking = ref(false);

  /**
   * Get the current user profile from Vuex store
   */
  const profile = computed(() => store.getters.getProfile || {});

  /**
   * Check if height is valid (greater than 0)
   */
  const hasValidHeight = computed(() => {
    const height = parseFloat(profile.value.height);
    return !isNaN(height) && height > 0;
  });

  /**
   * Check if weight is valid (greater than 0)
   */
  const hasValidWeight = computed(() => {
    const weight = parseFloat(profile.value.weight);
    return !isNaN(weight) && weight > 0;
  });

  /**
   * Check if DOB is valid (not null/empty and is a valid date)
   */
  const hasValidDob = computed(() => {
    const dob = profile.value.dob;
    if (!dob || dob === "" || dob === "0000-00-00" || dob === null) {
      return false;
    }
    // Check if it's a valid date
    const date = new Date(dob);
    return !isNaN(date.getTime());
  });

  /**
   * Check if all required fields are complete
   */
  const isProfileComplete = computed(() => {
    return hasValidHeight.value && hasValidWeight.value && hasValidDob.value;
  });

  /**
   * Get list of missing fields
   */
  const missingFields = computed(() => {
    const missing = [];
    if (!hasValidHeight.value) {
      missing.push({
        field: "height",
        label: "Height",
        description: "Your height in cm",
        icon: "ruler",
      });
    }
    if (!hasValidWeight.value) {
      missing.push({
        field: "weight",
        label: "Weight",
        description: "Your weight in kg",
        icon: "weight",
      });
    }
    if (!hasValidDob.value) {
      missing.push({
        field: "dob",
        label: "Date of Birth",
        description: "Your date of birth",
        icon: "calendar",
      });
    }
    return missing;
  });

  /**
   * Get completion percentage
   */
  const completionPercentage = computed(() => {
    let complete = 0;
    if (hasValidHeight.value) complete++;
    if (hasValidWeight.value) complete++;
    if (hasValidDob.value) complete++;
    return Math.round((complete / 3) * 100);
  });

  /**
   * Get current profile values
   */
  const profileValues = computed(() => ({
    height: profile.value.height || "",
    weight: profile.value.weight || "",
    dob: profile.value.dob || "",
    userId: profile.value.id,
  }));

  /**
   * Calculate age from DOB
   */
  const age = computed(() => {
    if (!hasValidDob.value) return null;
    const dob = new Date(profile.value.dob);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  });

  /**
   * Calculate BMI if height and weight are available
   */
  const bmi = computed(() => {
    if (!hasValidHeight.value || !hasValidWeight.value) return null;
    const heightM = parseFloat(profile.value.height) / 100;
    const weightKg = parseFloat(profile.value.weight);
    if (heightM <= 0) return null;
    return Math.round((weightKg / (heightM * heightM)) * 10) / 10;
  });

  return {
    // State
    isChecking,
    profile,

    // Validation
    hasValidHeight,
    hasValidWeight,
    hasValidDob,
    isProfileComplete,
    missingFields,
    completionPercentage,

    // Profile data
    profileValues,
    age,
    bmi,
  };
}

export default useProfileValidation;
